import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { RavIconCircled } from '@shared/icon/icon';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

interface ResetPolicyOption {
  key: string;
  label: string;
  value: number;
}

@Component({
  selector: 'rav-password-reset-policy',
  template: `
    <div class="grid-x grid-padding-x small-up-1 large-up-2">
      <div class="cell">
        <label
          >PASSWORD RESET POLICY
          <rav-icon
            [matTooltip]="
              'Require users to change passwords at set frequency.' +
              (disabled ? ' This feature is not available when E2E Encryption is enabled.' : '')
            "
            matTooltipPosition="above"
            [name]="ravIcon.Question"
            class="question"
          ></rav-icon>
        </label>
        <select [value]="selected" [disabled]="disabled" (change)="changePolicy(select.value)" #select>
          <option *ngFor="let option of policies" [value]="option.value"> {{ option.label }} </option>
        </select>
      </div>
    </div>
    <div class="grid-x grid-padding-x small-up-1 large-up-2">
      <div class="cell" *ngIf="isCustomPolicy && !disabled">
        <input type="text" placeholder="Enter custom days" [formControl]="customPolicyControl" />
        <div class="form-error is-visible" *ngIf="this.customPolicyControl.errors">
          Please enter a policy between 30 and 365 days
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .question {
        width: 0.75rem;
        height: 0.75rem;
        display: inline-flex;
        color: get-color(primary);
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PasswordResetPolicyComponent implements OnInit, OnChanges, OnDestroy {
  static readonly MIN_EXPIRE_DAYS = 30;
  static readonly MAX_EXPIRE_DAYS = 365;

  @Input() resetPolicy: number;
  @Input() disabled: boolean;
  @Output() updatePolicy = new EventEmitter<number | null>();

  policies: ResetPolicyOption[] = [
    {
      key: 'disabled',
      value: null,
      label: 'Disabled'
    },
    {
      key: '30',
      value: 30,
      label: '30 Days'
    },
    {
      key: '60',
      value: 60,
      label: '60 Days'
    },
    {
      key: '90',
      value: 90,
      label: '90 Days'
    },
    {
      key: 'custom',
      value: -1,
      label: 'Custom'
    }
  ];

  selected: number | null;

  ravIcon = RavIconCircled;
  customPolicyControl = new FormControl(null, [
    Validators.min(PasswordResetPolicyComponent.MIN_EXPIRE_DAYS),
    Validators.max(PasswordResetPolicyComponent.MAX_EXPIRE_DAYS)
  ]);
  private destroy$ = new Subject<boolean>();

  constructor() {}

  ngOnInit() {
    this.customPolicyControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        if (this.customPolicyControl.valid && value > 0) this.updatePolicy.emit(value);
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    const change = changes.resetPolicy;

    if (change) {
      const currentValue = changes.resetPolicy.currentValue;
      const value = currentValue ? +currentValue : currentValue;
      const option = this.policies.find((i) => i.value === value);

      if (!option) {
        this.selected = -1;
        this.enableCustomPolicyControl(true);
        this.customPolicyControl.patchValue(value, { emitEvent: false });
      } else {
        this.enableCustomPolicyControl(false);
        this.selected = option.value;
      }
    }
  }

  changePolicy(valueRaw: string) {
    const value = valueRaw === 'null' ? 0 : +valueRaw;
    switch (value) {
      case 0:
        this.enableCustomPolicyControl(false);
        this.updatePolicy.emit(null);
        break;
      case -1:
        this.enableCustomPolicyControl(true);
        this.customPolicyControl.patchValue(null, { emitEvent: false });
        break;
      default:
        this.enableCustomPolicyControl(false);
        this.updatePolicy.emit(+value);
        break;
    }
  }

  get isCustomPolicy() {
    return this.customPolicyControl.enabled;
  }

  private enableCustomPolicyControl(value: boolean) {
    if (value) {
      this.customPolicyControl.enable();
    } else {
      this.customPolicyControl.disable();
      this.customPolicyControl.patchValue(null, { emitEvent: false });
    }
  }
}
